import { useCallback, useMemo, useRef } from 'react';
import { UserPreferences } from '../types';
import { useUserPreferencesStore } from '../store';
import { DEFAULT_USER_PREFERENCES, UserPreferenceName } from '../constants';
import { UserPreferenceApiConnector } from '@/modules/api/UserPreferenceApiConnector/UserPreferenceApiConnector';
import { logger } from '@common/logging/logger';

const DEBOUNCE_TIME = 500;
type DebounceTimers = Record<string, NodeJS.Timeout>;

export const useUpdateUserPreferences = () => {
  const timersRef = useRef<DebounceTimers>({});

  const api = useMemo(() => UserPreferenceApiConnector.getInstance(), []);

  const createOrUpdateUserPreference = useCallback(
    async <T extends UserPreferenceName>(name: T, value: UserPreferences[T]) => {
      if (await api.update(name, value)) return;

      // Update failed (preference likely doesn't exist yet) — fall back to create.
      api.create(name, value).catch((createError) => {
        logger.error(`Error while creating/updating user preference ${name}:`, createError);
      });
    },
    [api],
  );

  const updateUserPreference = useCallback(
    <T extends UserPreferenceName>(name: T, value: UserPreferences[T], debounce = true) => {
      useUserPreferencesStore.getState().setPreference(name, value);

      if (debounce) {
        if (timersRef.current[name]) clearTimeout(timersRef.current[name]);

        timersRef.current[name] = setTimeout(() => {
          createOrUpdateUserPreference(name, value);
          delete timersRef.current[name];
        }, DEBOUNCE_TIME);
      } else {
        createOrUpdateUserPreference(name, value);
      }
    },
    [createOrUpdateUserPreference],
  );

  const resetUserPreference = useCallback(
    <T extends UserPreferenceName>(name: T) => {
      updateUserPreference(name, DEFAULT_USER_PREFERENCES[name] as UserPreferences[T], false);
    },
    [updateUserPreference],
  );

  return {
    updateUserPreference,
    resetUserPreference,
  };
};
